import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";

const fallbackImage = "/fpb.png";

function unique(values) {
  return ["All", ...Array.from(new Set(values.filter(Boolean)))];
}

export default function CampusMap() {
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api("/api/campus-locations")
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        setLocations(rows);
        setSelected(rows[0] || null);
      })
      .catch((err) => setError(err.message));
  }, []);

  const categories = useMemo(() => unique(locations.map((item) => item.category)), [locations]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return locations.filter((item) => {
      const categoryMatch = category === "All" || item.category === category;
      const queryMatch = !term || [item.name, item.category, item.description, item.contact].some((value) => String(value || "").toLowerCase().includes(term));
      return categoryMatch && queryMatch;
    });
  }, [locations, category, query]);

  return (
    <main className="bg-slate-50 px-4 py-16">
      <section className="mx-auto max-w-7xl">
        <div className="rounded-[2rem] bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-950 p-8 text-white shadow-xl md:p-12">
          <p className="text-sm font-black uppercase tracking-[0.25em] text-emerald-200">Explore Campus</p>
          <h1 className="mt-3 text-4xl font-black md:text-6xl">Interactive Campus Map</h1>
          <p className="mt-4 max-w-3xl text-emerald-50">Find schools, departments, centres, units, offices, library, ICT facilities, service points and virtual tour links from one clean public page.</p>
        </div>

        {error && <div className="mt-6 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

        <div className="mt-8 grid gap-4 md:grid-cols-[1fr_220px]">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search campus locations..."
            className="rounded-2xl border border-slate-200 bg-white px-5 py-4 font-semibold outline-none focus:border-emerald-500"
          />
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 font-semibold outline-none focus:border-emerald-500">
            {categories.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <div className="space-y-3 lg:max-h-[720px] lg:overflow-y-auto lg:pr-2">
            {filtered.map((location) => (
              <button
                key={location.id}
                onClick={() => setSelected(location)}
                className={`w-full rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-md ${selected?.id === location.id ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white"}`}
              >
                <div className="flex gap-4">
                  <img src={location.image_url || fallbackImage} alt="" className="h-16 w-16 rounded-xl object-cover" />
                  <div>
                    <h2 className="font-black text-slate-900">{location.name}</h2>
                    <p className="text-sm font-bold text-emerald-700">{location.category}</p>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">{location.description || "No description yet."}</p>
                  </div>
                </div>
              </button>
            ))}

            {filtered.length === 0 && <div className="rounded-2xl bg-white p-6 text-slate-500 shadow-sm">No campus location found.</div>}
          </div>

          <div className="lg:col-span-2">
            <div className="relative min-h-[520px] overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,.16),transparent_26%),radial-gradient(circle_at_80%_50%,rgba(15,23,42,.10),transparent_22%)]" />
              <div className="absolute inset-6 rounded-[2rem] border-2 border-dashed border-emerald-200" />
              {filtered.map((location) => (
                <button
                  key={location.id}
                  onClick={() => setSelected(location)}
                  title={location.name}
                  className={`absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white px-3 py-2 text-xs font-black shadow-lg transition hover:scale-110 ${selected?.id === location.id ? "bg-emerald-700 text-white" : "bg-slate-900 text-white"}`}
                  style={{ left: `${Number(location.map_x || 50)}%`, top: `${Number(location.map_y || 50)}%` }}
                >
                  {location.name?.slice(0, 2)?.toUpperCase() || "FP"}
                </button>
              ))}
            </div>

            <div className="mt-6 rounded-[2rem] bg-white p-6 shadow-sm ring-1 ring-slate-200">
              {selected ? (
                <div className="grid gap-6 md:grid-cols-[220px_1fr]">
                  <img src={selected.image_url || fallbackImage} alt={selected.name} className="h-56 w-full rounded-3xl object-cover" />
                  <div>
                    <p className="text-sm font-black uppercase tracking-wider text-emerald-700">{selected.category}</p>
                    <h2 className="mt-2 text-3xl font-black text-slate-900">{selected.name}</h2>
                    <p className="mt-3 text-slate-600">{selected.description || "No description has been added yet."}</p>
                    <div className="mt-5 grid gap-3 text-sm text-slate-600 md:grid-cols-2">
                      <p><b>Opening hours:</b> {selected.opening_hours || "Not specified"}</p>
                      <p><b>Contact:</b> {selected.contact || "Not specified"}</p>
                      <p><b>Latitude:</b> {selected.latitude || "N/A"}</p>
                      <p><b>Longitude:</b> {selected.longitude || "N/A"}</p>
                    </div>
                    {selected.virtual_tour_url && (
                      <a href={selected.virtual_tour_url} target="_blank" rel="noreferrer" className="mt-5 inline-flex rounded-xl bg-emerald-700 px-5 py-3 font-bold text-white hover:bg-emerald-800">Open Virtual Tour</a>
                    )}
                  </div>
                </div>
              ) : <p className="text-slate-500">No campus location added yet.</p>}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
